import { useState, useEffect, useRef } from 'react'
import { useDebounce } from 'hooks/useDebounce'
import { fetchCategorySearchApi } from 'hooks/category-manage'
import { fetchCollectionSearchApi } from 'hooks/collection'
import { fetchShowSearchApi } from 'hooks/shows-master'

interface ItemType {
    id: string
    name: string
    type: 'category' | 'collection' | 'show'
}

interface Props {
    itemType: 'category' | 'collection' | 'show'
    value?: ItemType | null
    onSelect: (item: ItemType | null) => void
    placeholder?: string
}

export function SearchableItemTypeDropdown({
    itemType,
    value,
    onSelect,
    placeholder = `Search ${itemType}...`,
}: Props) {
    const [query, setQuery] = useState(value?.name || "")
    const [options, setOptions] = useState<ItemType[]>([])
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const isUserTypingRef = useRef(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const debouncedQuery = useDebounce(query, 400)

    // Update query when value changes (prefill/edit mode)
    useEffect(() => {
        if (isUserTypingRef.current) return

        if (value?.name && query !== value.name) {
            setQuery(value.name)
        }

        if (!value && query !== "") {
            setQuery("")
        }
    }, [value?.id, value?.name])

    // Fetch items based on item type
    useEffect(() => {
        if (!debouncedQuery.trim()) {
            setOptions([])
            return
        }

        const load = async () => {
            try {
                setLoading(true)
                let results: any[] = []

                switch (itemType) {
                    case 'category':
                        results = await fetchCategorySearchApi(debouncedQuery)
                        break
                    case 'collection':
                        results = await fetchCollectionSearchApi(debouncedQuery)
                        break
                    case 'show':
                        results = await fetchShowSearchApi(debouncedQuery)
                        break
                }

                const formattedOptions = results.map((item) => ({
                    id: item.id,
                    name: item.name,
                    type: itemType,
                }))

                setOptions(formattedOptions)
            } catch (err) {
                console.error(`Error fetching ${itemType}:`, err)
                setOptions([])
            } finally {
                setLoading(false)
            }
        }

        load()
    }, [debouncedQuery, itemType])

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!dropdownRef.current?.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener("mousedown", handler)
        return () => document.removeEventListener("mousedown", handler)
    }, [])

    const handleSelect = (item: ItemType) => {
        isUserTypingRef.current = false
        onSelect(item)
        setQuery(item.name)
        setOpen(false)
    }

    return (
        <div ref={dropdownRef} className="relative w-full">
            <input
                value={query}
                placeholder={placeholder}
                onChange={(e) => {
                    isUserTypingRef.current = true
                    setQuery(e.target.value)
                    setOpen(true)
                    onSelect(null)
                }}
                onFocus={() => {
                    setOpen(true)
                    if (value?.name && query === value.name) {
                        isUserTypingRef.current = false
                    }
                }}
                onBlur={() => {
                    setTimeout(() => {
                        isUserTypingRef.current = false
                    }, 200)
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
            />

            {open && (
                <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    {loading && (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                            Searching...
                        </div>
                    )}

                    {!loading && options.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                            No results found
                        </div>
                    )}

                    {options.map((item) => (
                        <div
                            key={item.id}
                            onClick={() => handleSelect(item)}
                            className="cursor-pointer px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-slate-700"
                        >
                            {item.name}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
